import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Star, MessageSquare, Copy, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVIS_PRE_FORMULE = "Je suis très satisfait(e) de l'installation et du professionnalisme de l'équipe. Le travail a été réalisé dans les temps, avec soin et propreté. Les explications sur le fonctionnement des équipements étaient claires. Je recommande vivement cette entreprise pour leur sérieux et leur expertise.";

export default function Step5Avis({ data, onNext, userInfo }) {
  const [skipAvis, setSkipAvis] = useState(false);
  const [avisText, setAvisText] = useState(AVIS_PRE_FORMULE);
  const [rating, setRating] = useState(5);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [reviewUrls, setReviewUrls] = useState({ google_review_url: '', trustpilot_url: '' });

  useEffect(() => {
    if (data) {
      setSkipAvis(data.skip_avis || false);
      setAvisText(data.avis_text || AVIS_PRE_FORMULE);
      setRating(data.rating || 5);
    }
  }, [data]);

  // Fetch company review URLs
  useEffect(() => {
    const fetchReviewUrls = async () => {
      if (!userInfo?.company_id) return;
      try {
        const token = localStorage.getItem('user_token');
        const res = await fetch(`${API_URL}/api/company/review-urls/${userInfo.company_id}`);
        if (res.ok) {
          const data = await res.json();
          setReviewUrls(data);
          // Auto-select first available platform
          if (data.google_review_url) setSelectedPlatform('google');
          else if (data.trustpilot_url) setSelectedPlatform('trustpilot');
        }
      } catch (e) {
        console.warn('Could not fetch review URLs:', e);
      }
    };
    fetchReviewUrls();
  }, [userInfo]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      // Fallback for older browsers
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        return true;
      } catch (e2) {
        return false;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (skipAvis) {
      toast.success('Étape avis passée');
      onNext({ skip_avis: true, avis_submitted: false });
      return;
    }
    
    if (!avisText.trim()) {
      toast.error('Veuillez rédiger un avis ou cocher "Je ne souhaite pas laisser d\'avis"');
      return;
    }

    // Copy text to clipboard and open review platform
    if (selectedPlatform && avisText.trim()) {
      const copied = await copyToClipboard(avisText.trim());
      
      let platformUrl = null;
      if (selectedPlatform === 'google' && reviewUrls.google_review_url) {
        platformUrl = reviewUrls.google_review_url;
      } else if (selectedPlatform === 'trustpilot' && reviewUrls.trustpilot_url) {
        platformUrl = reviewUrls.trustpilot_url;
      }

      if (copied) {
        toast.success('Texte copié ! Collez-le sur la page qui s\'ouvre', { duration: 5000 });
      }
      
      if (platformUrl) {
        window.open(platformUrl, '_blank', 'noopener,noreferrer');
      }
    }

    toast.success('Avis enregistré');
    onNext({
      skip_avis: false,
      avis_submitted: true,
      avis_text: avisText,
      rating: rating,
      review_platform: selectedPlatform,
    });
  };

  const platforms = [
    {
      id: 'google',
      name: 'Google',
      url: reviewUrls.google_review_url,
      color: 'bg-white border-blue-300 hover:border-blue-500',
      activeColor: 'bg-blue-50 border-blue-500 ring-1 ring-blue-500',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      ),
    },
    {
      id: 'trustpilot',
      name: 'Trustpilot',
      url: reviewUrls.trustpilot_url,
      color: 'bg-white border-green-300 hover:border-green-500',
      activeColor: 'bg-green-50 border-green-500 ring-1 ring-green-500',
      icon: (
        <svg className="w-6 h-6" viewBox="0 0 24 24">
          <path fill="#00B67A" d="M12 0L15.09 7.36L23 8.54L17.5 13.97L18.82 22L12 18.27L5.18 22L6.5 13.97L1 8.54L8.91 7.36L12 0Z"/>
        </svg>
      ),
    },
  ];

  const availablePlatforms = platforms.filter(p => p.url);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-2">
          <MessageSquare className="w-8 h-8 text-accent" strokeWidth={1.5} />
        </div>
        <h2 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Votre avis
        </h2>
        <p className="text-base text-foreground/80">
          Partagez votre expérience (optionnel)
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Option: Skip Avis */}
        <div className="bg-card border border-border rounded-lg p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="skip-avis-checkbox"
              type="checkbox"
              checked={skipAvis}
              onChange={(e) => {
                setSkipAvis(e.target.checked);
                if (e.target.checked) {
                  setAvisText('');
                  setRating(0);
                  setSelectedPlatform(null);
                }
              }}
              className="w-5 h-5 mt-1 rounded border-input text-accent focus:ring-accent"
            />
            <div className="flex-1">
              <span className="text-sm font-medium block mb-1">
                Je ne souhaite pas laisser d'avis
              </span>
              <span className="text-xs text-foreground/60">
                Cochez cette case pour passer directement à l'étape suivante
              </span>
            </div>
          </label>
        </div>

        {!skipAvis && (
          <>
            {/* Rating Stars */}
            <div className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Note globale
              </h3>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    data-testid={`star-${star}`}
                    onClick={() => setRating(star)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-10 h-10 ${star <= rating ? 'text-accent fill-accent' : 'text-foreground/20'}`}
                      strokeWidth={1.5}
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-foreground/60">
                {rating === 5 && 'Excellent !'}
                {rating === 4 && 'Très bien'}
                {rating === 3 && 'Bien'}
                {rating === 2 && 'Moyen'}
                {rating === 1 && 'À améliorer'}
              </p>
            </div>

            {/* Avis Text */}
            <div className="bg-card border border-border rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Votre témoignage
              </h3>
              <div className="space-y-2">
                <label className="text-sm text-foreground/60">Avis pré-formulé (modifiable)</label>
                <textarea
                  data-testid="avis-textarea"
                  value={avisText}
                  onChange={(e) => setAvisText(e.target.value)}
                  rows={6}
                  className="w-full bg-white border border-input rounded-md p-4 text-sm focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none resize-none"
                  placeholder="Rédigez votre avis..."
                />
                <p className="text-xs text-foreground/60">
                  Vous pouvez modifier le texte selon votre expérience
                </p>
              </div>
            </div>

            {/* Platform Selection */}
            {availablePlatforms.length > 0 && (
              <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Publier aussi sur
                </h3>
                <p className="text-sm text-foreground/60">
                  Votre avis sera copié automatiquement, il vous suffira de le coller
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {availablePlatforms.map((platform) => (
                    <button
                      key={platform.id}
                      type="button"
                      data-testid={`platform-${platform.id}`}
                      onClick={() => setSelectedPlatform(selectedPlatform === platform.id ? null : platform.id)}
                      className={`flex items-center justify-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        selectedPlatform === platform.id ? platform.activeColor : platform.color
                      }`}
                    >
                      {platform.icon}
                      <span className="font-medium text-sm">{platform.name}</span>
                    </button>
                  ))}
                </div>
                {selectedPlatform && (
                  <div className="flex items-center gap-2 text-xs text-foreground/60 bg-accent/5 rounded-md p-3">
                    <Copy className="w-4 h-4 flex-shrink-0" />
                    <span>Au clic sur "Continuer", le texte sera copié et la page {platforms.find(p => p.id === selectedPlatform)?.name} s'ouvrira</span>
                  </div>
                )}
              </div>
            )}

            {/* Info Note */}
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
              <p className="text-sm text-foreground/80">
                <span className="font-medium">Note :</span> Votre avis sera utilisé dans le cadre de notre communication.
                Il pourra être publié sur notre site internet et nos supports marketing.
              </p>
            </div>
          </>
        )}

        {/* Submit Button */}
        <button
          data-testid="next-step-btn"
          type="submit"
          className="w-full h-14 bg-accent text-accent-foreground text-lg font-medium rounded-md hover:bg-accent/90 border-b-2 border-accent-foreground/20 active:border-b-0 active:translate-y-[2px] transition-all flex items-center justify-center gap-2"
        >
          {selectedPlatform && !skipAvis ? (
            <>
              <ExternalLink className="w-5 h-5" />
              Copier et publier sur {platforms.find(p => p.id === selectedPlatform)?.name}
            </>
          ) : (
            'Continuer'
          )}
        </button>
      </form>
    </div>
  );
}
